import React from 'react';
import { FormattedMessage } from 'react-intl';

import {
  BedSmallSvg,
  BedroomSvg,
  CarSvg,
  ClockSvg,
  InfoSvg,
  KitchenSvg,
  PoolSvg,
  WifiSvg,
} from '@assets';
import { IDescriptionTile } from '@utils';

import './style.scss';

interface IProps {
  tiles: IDescriptionTile[];
  // Null where the surrounding section already carries a heading, as under the map.
  titleId?: string | null;
}

interface ITileProps {
  tile: IDescriptionTile;
}

const INITIAL_TILE_COUNT = 6;
const INITIAL_ITEM_COUNT = 5;
const OVERFLOW_TOLERANCE = 1;

const renderTileIcon = (title: string | null): React.ReactNode => {
  if (!title) {
    return <InfoSvg />;
  }

  if (/bedroom/i.test(title)) {
    return <BedroomSvg size="18" />;
  }
  if (/\bbed\b/i.test(title)) {
    return <BedSmallSvg size="18" />;
  }
  if (/kitchen|provision|dining/i.test(title)) {
    return <KitchenSvg />;
  }
  if (/outdoor|pool|patio|garden/i.test(title)) {
    return <PoolSvg />;
  }
  if (/check-?in|check-?out|quiet|hours/i.test(title)) {
    return <ClockSvg />;
  }
  if (/parking|getting around|access|transport/i.test(title)) {
    return <CarSvg />;
  }
  if (/wifi|internet|work den|living room|lounge/i.test(title)) {
    return <WifiSvg />;
  }

  return <InfoSvg />;
};

const QuintessInfoTile: React.FunctionComponent<ITileProps> = ({ tile }: ITileProps) => {
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const [isOverflowing, setIsOverflowing] = React.useState<boolean>(false);

  const items = tile.items || [];
  const isList = !!items.length;

  // Measured while still clamped, since expanding removes the clamp and would
  // report the tile as no longer overflowing. A list is cut by item instead, which
  // is both exact and something a line clamp cannot do across list items.
  React.useLayoutEffect(() => {
    const node = bodyRef.current;

    if (node && !isList) {
      setIsOverflowing(node.scrollHeight > node.clientHeight + OVERFLOW_TOLERANCE);
    }
  }, [tile.body, isList]);

  const handleToggle = (): void => setIsExpanded((expanded) => !expanded);
  const visibleItems = isExpanded ? items : items.slice(0, INITIAL_ITEM_COUNT);
  const hasMore = isList ? items.length > INITIAL_ITEM_COUNT : isOverflowing;

  return (
    <div className="quintess-info-tiles__tile">
      <div className="quintess-info-tiles__tile-header">
        <span className="quintess-info-tiles__tile-icon">{renderTileIcon(tile.title)}</span>
        {!!tile.title && <h5 className="quintess-info-tiles__tile-title">{tile.title}</h5>}
      </div>

      {isList ? (
        <ul className="quintess-info-tiles__tile-list">
          {visibleItems.map((item, index) => (
            <li key={index} className="quintess-info-tiles__tile-item">
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <div
          ref={bodyRef}
          className={`quintess-info-tiles__tile-body ${isExpanded ? 'expanded' : ''}`}
        >
          {tile.body}
        </div>
      )}

      {!!hasMore && (
        <button type="button" className="quintess-info-tiles__tile-toggle" onClick={handleToggle}>
          <FormattedMessage id={isExpanded ? 'show.less' : 'show.more'} />
        </button>
      )}
    </div>
  );
};

export const QuintessInfoTiles: React.FunctionComponent<IProps> = ({
  tiles,
  titleId = 'about.this.property',
}: IProps) => {
  const [showAll, setShowAll] = React.useState<boolean>(false);

  if (!tiles.length) {
    return null;
  }

  const visibleTiles = showAll ? tiles : tiles.slice(0, INITIAL_TILE_COUNT);
  const hasHiddenTiles = tiles.length > INITIAL_TILE_COUNT;

  return (
    <div className="quintess-info-tiles">
      {!!titleId && (
        <h4 className="quintess-info-tiles__title">
          <FormattedMessage id={titleId} />
        </h4>
      )}

      <div className="quintess-info-tiles__grid">
        {visibleTiles.map((tile) => (
          <QuintessInfoTile key={tile.id} tile={tile} />
        ))}
      </div>

      {!!hasHiddenTiles && (
        <button
          type="button"
          className="quintess-info-tiles__show-all"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? (
            <FormattedMessage id="show.less" />
          ) : (
            <FormattedMessage id="show.all.details" values={{ count: tiles.length }} />
          )}
        </button>
      )}
    </div>
  );
};
